import React, { useState } from 'react';
import { COLORS } from '../../utils/theme';

const SubmissionForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    surname: '',
    firstName: '',
    institution: '',
    project: '',
    outputType: '',
    year: '',
    reference: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'year':
        const yearNum = parseInt(value);
        const currentYear = new Date().getFullYear();
        if (!/^\d{4}$/.test(value) || yearNum < 1900 || yearNum > currentYear) {
          return `Please enter a valid year between 1900 and ${currentYear}`;
        }
        break;
      case 'reference':
        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
          return 'Please enter a valid URL starting with http:// or https://';
        }
        break;
      case 'surname':
      case 'firstName':
        if (value.length < 2) {
          return 'Must be at least 2 characters';
        }
        break;
      case 'institution':
        if (value.length < 3) {
          return 'Institution name must be at least 3 characters';
        }
        break;
      case 'project':
        if (value.length < 5) {
          return 'Project name must be at least 5 characters';
        }
        break;
      default:
        break;
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Store the submission in localStorage
      const submissions = JSON.parse(localStorage.getItem('researchSubmissions') || '[]');
      const newSubmission = {
        ...formData,
        id: Date.now(),
        submittedAt: new Date().toISOString(),
        status: 'pending'
      };
      submissions.push(newSubmission);
      localStorage.setItem('researchSubmissions', JSON.stringify(submissions));

      setSuccess(true);
      setFormData({
        surname: '',
        firstName: '',
        institution: '',
        project: '',
        outputType: '',
        year: '',
        reference: ''
      });

      // Close form after success message
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);

    } catch (err) {
      setSubmitError('Failed to submit research output. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary.dark }}>
            Submit New Research Output
          </h2>
          
          {success ? (
            <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
              Research output submitted successfully!
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Surname *</label>
                  <input
                    type="text"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const error = validateField('surname', e.target.value);
                      setErrors(prev => ({ ...prev, surname: error }));
                    }}
                    required
                    className={`w-full p-2 border rounded focus:ring-2 ${errors.surname ? 'border-red-500' : ''}`}
                    style={{ borderColor: errors.surname ? 'red' : COLORS.primary.main }}
                  />
                  {errors.surname && <p className="text-red-500 text-xs mt-1">{errors.surname}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const error = validateField('firstName', e.target.value);
                      setErrors(prev => ({ ...prev, firstName: error }));
                    }}
                    required
                    className={`w-full p-2 border rounded focus:ring-2 ${errors.firstName ? 'border-red-500' : ''}`}
                    style={{ borderColor: errors.firstName ? 'red' : COLORS.primary.main }}
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Institution *</label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const error = validateField('institution', e.target.value);
                    setErrors(prev => ({ ...prev, institution: error }));
                  }}
                  required
                  className={`w-full p-2 border rounded focus:ring-2 ${errors.institution ? 'border-red-500' : ''}`}
                  style={{ borderColor: errors.institution ? 'red' : COLORS.primary.main }}
                />
                {errors.institution && <p className="text-red-500 text-xs mt-1">{errors.institution}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Project Name *</label>
                <input
                  type="text"
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const error = validateField('project', e.target.value);
                    setErrors(prev => ({ ...prev, project: error }));
                  }}
                  required
                  className={`w-full p-2 border rounded focus:ring-2 ${errors.project ? 'border-red-500' : ''}`}
                  style={{ borderColor: errors.project ? 'red' : COLORS.primary.main }}
                />
                {errors.project && <p className="text-red-500 text-xs mt-1">{errors.project}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Output Type *</label>
                  <select
                    name="outputType"
                    value={formData.outputType}
                    onChange={handleChange}
                    required
                    className={`w-full p-2 border rounded focus:ring-2 ${errors.outputType ? 'border-red-500' : ''}`}
                    style={{ borderColor: errors.outputType ? 'red' : COLORS.primary.main }}
                  >
                    <option value="">Select type...</option>
                    {[
                      'Article',
                      'PhD thesis',
                      'Research project',
                      'Report',
                      'Editorial',
                      'Book',
                      'Letter'
                    ].map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.outputType && <p className="text-red-500 text-xs mt-1">{errors.outputType}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Year *</label>
                  <input
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const error = validateField('year', e.target.value);
                      setErrors(prev => ({ ...prev, year: error }));
                    }}
                    required
                    placeholder="YYYY"
                    className={`w-full p-2 border rounded focus:ring-2 ${errors.year ? 'border-red-500' : ''}`}
                    style={{ borderColor: errors.year ? 'red' : COLORS.primary.main }}
                  />
                  {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Reference/URL *</label>
                <input
                  type="url"
                  name="reference"
                  value={formData.reference}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const error = validateField('reference', e.target.value);
                    setErrors(prev => ({ ...prev, reference: error }));
                  }}
                  required
                  placeholder="https://"
                  className={`w-full p-2 border rounded focus:ring-2 ${errors.reference ? 'border-red-500' : ''}`}
                  style={{ borderColor: errors.reference ? 'red' : COLORS.primary.main }}
                />
                {errors.reference && <p className="text-red-500 text-xs mt-1">{errors.reference}</p>}
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded p-4 text-red-600">
                  {submitError}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded"
                  style={{ 
                    backgroundColor: '#fff',
                    color: COLORS.primary.dark,
                    border: `1px solid ${COLORS.primary.dark}`
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 rounded text-white"
                  style={{ 
                    backgroundColor: loading ? '#ccc' : COLORS.primary.main,
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmissionForm;